import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface ScrollAnimationProps {
    children: React.ReactNode;
    className?: string;
    animation?: "fade-in" | "slide-up" | "scale-in" | "fade-in-right" | "fade-in-left";
    delay?: number; // in seconds
    duration?: number; // in seconds
    threshold?: number; // 0 to 1
    once?: boolean;
}

const ScrollAnimation: React.FC<ScrollAnimationProps> = ({
    children,
    className,
    animation = "slide-up",
    delay = 0,
    duration = 0.5,
    threshold = 0.1,
    once = true,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (once && ref.current) {
                        observer.unobserve(ref.current);
                    }
                } else if (!once) {
                    setIsVisible(false);
                }
            },
            {
                threshold,
                rootMargin: "0px 0px -50px 0px", // Trigger a bit before the element is fully in view
            }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [threshold, once]);

    const animationClass = {
        "fade-in": "animate-fade-in",
        "slide-up": "animate-slide-up",
        "scale-in": "animate-scale-in",
        "fade-in-right": "animate-fade-in-right",
        "fade-in-left": "animate-fade-in-left",
    }[animation];

    return (
        <div
            ref={ref}
            className={cn(
                "transition-opacity duration-500",
                isVisible ? "opacity-100" : "opacity-0",
                isVisible && animationClass,
                className
            )}
            style={{
                animationDelay: `${delay}s`,
                animationDuration: `${duration}s`,
                animationFillMode: "both",
            }}
        >
            {children}
        </div>
    );
};

export default ScrollAnimation;
